import { apiClient } from '../client';
import type { ProductUnavailability } from '../products/types';
import { formatDateParam, formatShortDate } from '../../utils/formatters';
import { deriveEventChips } from '../../components/common/eventChips';
import { composeEventAddress } from '../../utils/eventAddress';
import type { EventListItem, EventTab } from '../../components/EventManagement/types';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Event statuses that count toward pickup-alert capacity, mirroring the backend
 * `FulfilmentEvent::PUBLISHED_EVENT_STATES`. An event in any other status (e.g.
 * `draft`, `cancelled`) is excluded from cap totals.
 */
export const PUBLISHED_EVENT_STATES: readonly string[] = [
  'confirmed',
  'preparing',
  'ready',
  'delivered',
];

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EventSalesperson {
  id: number;
  name: string;
  avatar_url: string | null;
}

export interface EventOutlet {
  id: number;
  name: string;
}

export interface EventSpecialist {
  name: string;
  phone?: string;
  label?: string;
}

export interface EventAddress {
  id: number;
  line1: string;
  line2: string | null;
  city: string | null;
  postal_code: string | null;
  full_address: string;
}

export interface EventTimingDetails {
  pickup_time: string | null;
  setup_time: string | null;
  serving_time: string | null;
  teardown_time: string | null;
  ready_to_eat_at: string | null;
  consume_by: string | null;
}

export interface EventLogisticsInfo {
  venue_name: string | null;
  venue_address: string | null;
  venue_unit: string | null;
  postal_code: string | null;
  // Event-owned copy (legacy / fallback). The current source of truth is the
  // linked Location — see `sourced_location_notes` on ApiEvent.
  location_notes: string | null;
  lines: number | null;
  ways: number | null;
}

export interface EventHostInfo {
  primary_host_name: string | null;
  primary_host_number: string | null;
  secondary_host_name: string | null;
  secondary_host_number: string | null;
  customer_notes: string | null;
}

export interface EventRequirements {
  toggles: string[];
  stairs: boolean;
}

export interface EventMetadata {
  form?: string;
  product_line?: string;
  sub_type?: string;
  tags?: string[];
  occasions?: string[];
  occasions_note?: string | null;
  description?: string;
  special_instructions?: string;
  event_requirements?: EventRequirements;
  [key: string]: unknown;
}

// Slim inventory/container shapes for the event-editor Inventory column
// (GRAIN-2433). Deliberately NOT the products-API ApiInventorySummary /
// ApiProductContainer: the serializer sends only {id, name} for the inventory
// (no category/image_url), so reusing the full types would type-claim fields
// that arrive undefined. Shared with the preset-menu item product type.
export interface ApiInventoryRef {
  id: number;
  name: string;
}

export interface ApiProductContainerRef {
  id: number;
  capacity: number | null;
  minimum_pax_required: number | null;
  inventory: ApiInventoryRef | null;
}

export interface ApiProductInventoryRef {
  id: number;
  inventory: ApiInventoryRef | null;
}

export interface ApiEventLineItemProduct {
  id: number;
  name: string;
  form: string;
  category: string | null;
  diet_tags: string[];
  chef_recommended: boolean;
  bcrs: boolean;
  production_notes: string | null;
  fulfilment_notes: string | null;
  price_including_gst: number | null;
  price_excluding_gst: number | null;
  product_containers: ApiProductContainerRef[];
  product_inventories: ApiProductInventoryRef[];
}

export interface ApiEventLineItemResaleItem {
  id: number;
  name: string;
  identifier: string | null;
  supplier: string | null;
  /** The resale master's live "item note" (ResaleItemSerializer#to_summary_h). */
  notes: string | null;
  max_quantity: number | null;
}

export interface ApiEventLineItem {
  id: number;
  item_type: string | null;
  item: ApiEventLineItemProduct | ApiEventLineItemResaleItem | null;
  quantity: number;
  unit_price: number;
  position: number;
  // Per-line overrides (labelled "Production"/"Fulfilment notes" in the UI); match
  // Papercut's field names so the mappers are symmetric pass-throughs.
  dish_instructions_food: string | null;
  dish_instructions_ops: string | null;
  external_notes: string | null;
  // Dish's standard note: derived live from the product for menu rows, stored for resale.
  internal_notes: string | null;
  internal_notes_ops: string | null;
  special_request: string | null;
  // Products only: why the dish can't be ordered on the event's serving date; null when it can.
  unavailability?: ProductUnavailability | null;
}

export type ApiPriceModifierType = 'Discount' | 'Surcharge';

export interface ApiPriceModifierSummary {
  id: number;
  name: string;
}

export interface ApiPriceModifier {
  id: number;
  modifier_type: ApiPriceModifierType | null;
  modifier: ApiPriceModifierSummary | null;
  name: string;
  description: string | null;
  calculated_amount: number;
  applied_amount: number;
  position: number;
  auto_applied: boolean;
  // System-applied at website checkout; cannot be removed from the event form.
  locked: boolean;
}

export interface ApiEventModifier {
  name: string;
  amount: number;
  note: string | null;
}

/**
 * Quotation-money breakdown computed server-side by Events::Breakdown —
 * the same source the quotation/confirmation PDFs read. Dollars.
 */
export interface ApiEventBreakdown {
  food: number;
  line_items: number;
  delivery: number;
  /** Linked Spine order's pre-GST amount (0 when unlinked or the feature is off). */
  spine_items: number;
  fees: ApiEventModifier[];
  discounts: ApiEventModifier[];
  subtotal: number;
  gst: number;
  total: number;
}

/** How the customer receives the food (GRAIN-1727). Mirrors FulfilmentEvent::FulfilmentTypes. */
export type FulfilmentType = 'delivery' | 'self_pickup';

export type ApiEventSummary = Pick<ApiEvent, 'id' | 'event_id'>;

export interface ApiEvent {
  id: number;
  event_id: string;
  event_code: string | null;
  event_type: string;
  fulfilment_type: FulfilmentType;
  form: string | null;
  product_line: string | null;
  product_line_exempt: boolean;
  event_status: string;
  scheduled_at: string | null;
  delivered_at: string | null;
  pre_setup_date_time: string | null;
  pax_count: number | null;
  price_per_pax: number | null;
  delivery_fee: number;
  bcrs_deposit: number;
  /** True when the deposit is fixed at the agreed price (paid, or confirmed pre-cutover). */
  bcrs_deposit_frozen: boolean;
  gst_rate: number;
  breakdown: ApiEventBreakdown;
  line_count: number | null;
  way_count: number | null;
  pickup_time: string | null;
  serving_time: string | null;
  outlet: EventOutlet | null;
  address: EventAddress | null;
  venue_address: string | null;
  venue_unit: string | null;
  postal_code: string | null;
  order_id: number | null;
  sales_order_number: string | null;
  salesperson: EventSalesperson | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_id?: number | null;
  organisation_id?: number | null;
  organisation_name: string | null;
  tags: string[];
  /** Any closed operating hour overlaps the event's date. */
  kitchen_closed: boolean;
  /** metadata.event_requirements.toggles contains security_clearance. */
  security_clearance: boolean;
  specialists: EventSpecialist[];
  created_at: string;
  updated_at: string;
  // Detail fields (present on order-scoped endpoint)
  timing_details?: EventTimingDetails;
  logistics_info?: EventLogisticsInfo;
  host_info?: EventHostInfo;
  metadata?: EventMetadata;
  line_items?: ApiEventLineItem[];
  price_modifiers?: ApiPriceModifier[];
  // Customer note sourced from the linked Contact; Edit Event saves changes
  // back to that Contact.
  sourced_customer_notes?: string | null;
  // Organisation note sourced from the linked Organisation; Edit Event saves
  // changes back to that Organisation.
  sourced_organisation_notes?: string | null;
  // Location note sourced from the Location matching the event's postal code;
  // Edit Event saves changes back to that Location. Seeds the note field and
  // its dirty baseline so a reload isn't spuriously dirty.
  sourced_location_notes?: string | null;
  contact_default_billing_address?: {address: string; postal_code: string | null} | null;
  // Linked Spine (meal-box) order, live-read from Spine. null when unlinked or
  // the linking feature is off; identifier-only when the order vanished from
  // Spine after linking.
  spine_order?: ApiSpineOrder | null;
}

export interface ApiSpineOrderLineItem {
  name: string | null;
  quantity: number;
  /** Before GST. */
  unit_price: number;
  /** Before GST. */
  amount: number;
}

export interface ApiSpineOrder {
  identifier: string;
  /** Spine admin's order search for this identifier; null when the Spine endpoint isn't configured. */
  admin_url?: string | null;
  state?: string;
  customer_name?: string | null;
  serving_at?: string | null;
  delivery_address?: string | null;
  line_items?: ApiSpineOrderLineItem[];
  /** GST-exempt BCRS deposit in dollars, from the order's current invoice. */
  bcrs_deposit?: number;
}

/**
 * One reason a draft event can't publish yet, as computed server-side by
 * `FulfilmentEvent::PublishBlockers`. Rides beside `event` on the update
 * response and on the edit page's bootstrap props — never derived client-side,
 * so the card can't drift from the validations that actually gate publishing.
 *
 * Tagged by `kind` because the three rows are genuinely different shapes, and
 * because the section a row belongs to is a server-side fact: a labelled field
 * names a form field and its current value, while the hard cap and the
 * catch-all each carry a whole sentence and land in their own section.
 */
export type PublishBlocker =
  | FieldPublishBlocker
  | CapacityPublishBlocker
  | InactiveItemPublishBlocker
  | OtherPublishBlocker;

export interface FieldPublishBlocker {
  kind: 'field';
  field: string;
  code: string;
  current_value: string | null;
}

/** The pickup hard cap. The message names the window and the limit. */
export interface CapacityPublishBlocker {
  kind: 'capacity';
  message: string;
}

/** A Product line item inactive on the event's serving date. One row per item, naming it. */
export interface InactiveItemPublishBlocker {
  kind: 'inactive_item';
  message: string;
}

/** Any other gate failure — the validator's own sentence, as the 422 would say it. */
export interface OtherPublishBlocker {
  kind: 'other';
  message: string;
}

export interface PaginationResponse {
  page: number;
  per_page: number;
  total: number;
  sort_by: string;
  sort_direction: string;
}

export interface EventsListResponse {
  events: ApiEvent[];
  pagination: PaginationResponse;
  total_unfiltered: number;
}

// ─── Params ─────────────────────────────────────────────────────────────────

export interface EventsListParams {
  page?: number;
  perPage?: number;
  sortBy?: 'scheduled_at' | 'created_at' | 'event_id';
  sortDirection?: 'asc' | 'desc';
  eventStatuses?: string[];
  eventTypes?: string[];
  eventDate?: string;
  salespersonIds?: number[];
  orderStatuses?: string[];
  q?: string;
}

// ─── Param helpers ──────────────────────────────────────────────────────────

export function buildEventDateParam(tab: EventTab, selectedDate: Date | undefined): string | undefined {
  if (selectedDate) return formatDateParam(selectedDate);
  if (tab === 'today') return formatDateParam(new Date());
  if (tab === 'tomorrow') {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return formatDateParam(d);
  }
  return undefined;
}

// ─── Query keys ─────────────────────────────────────────────────────────────

export const eventsQueryKeys = {
  all: ['events'] as const,
  lists: () => [...eventsQueryKeys.all, 'list'] as const,
  list: (params?: EventsListParams) => [...eventsQueryKeys.lists(), params] as const,
  orderEvents: (orderId: number) => [...eventsQueryKeys.all, 'order', orderId] as const,
};

// ─── API functions ──────────────────────────────────────────────────────────

export async function getEvents(params?: EventsListParams): Promise<EventsListResponse> {
  const response = await apiClient.get<EventsListResponse>('/api/events', { params });
  return response.data;
}

export async function getOrderEvents(orderId: number): Promise<ApiEvent[]> {
  const response = await apiClient.get<{ events: ApiEvent[] }>(`/api/orders/${orderId}/events`);
  return response.data.events;
}

export * from './mutations';

// ─── Mapper ─────────────────────────────────────────────────────────────────

const EVENT_TYPE_LABELS: Record<string, string> = {
  buffet: 'Buffet',
  mini_buffet: 'Mini Buffet',
  gathering_box: 'Gathering Box',
  meals_on_demand: 'Meals on Demand',
};

export function mapEventToListItem(event: ApiEvent): EventListItem {
  return {
    id: String(event.id),
    eventIdentifier: event.event_id,
    orderId: event.order_id ?? null,
    salesOrderNumber: event.sales_order_number ?? '',
    eventType: EVENT_TYPE_LABELS[event.event_type] ?? event.event_type,
    eventTypeKey: event.event_type,
    productLine: event.product_line ?? null,
    eventStatus: event.event_status,
    form: event.form ?? null,
    guestCount: event.pax_count ?? 0,
    lineCount: event.line_count ?? undefined,
    wayCount: event.way_count ?? undefined,
    eventDate: event.scheduled_at ?? '',
    eventDateFormatted: event.scheduled_at ? formatShortDate(event.scheduled_at) : '',
    pickupTime: event.pickup_time ?? '',
    serviceTime: event.serving_time ?? '',
    tags: event.tags ?? [],
    kitchenClosed: event.kitchen_closed ?? false,
    securityClearance: event.security_clearance ?? false,
    preSetupDateTime: event.pre_setup_date_time ?? null,
    chips: deriveEventChips(event),
    fulfilmentType: event.fulfilment_type,
    deliveryAddress: composeEventAddress(event) || undefined,
    postalCode: event.postal_code ?? null,
    contactName: event.contact_name ?? '',
    contactPhone: event.contact_phone ?? '',
    organisationName: event.organisation_name ?? undefined,
    salesperson: {
      name: event.salesperson?.name ?? '',
      avatarUrl: event.salesperson?.avatar_url ?? undefined,
    },
    specialists: event.specialists ?? [],
  };
}
